import { ReactNode, useState } from "react";
import { MenuDivider, MenuItem, Tooltip } from "@chakra-ui/react";
import { useTenantLabels } from "@tether-web-portal/utils/tenantLabels";
import { ActiveTenantSummary, TenantBlocked } from "@tether-web-portal/types/PropertyTenant";
import { useGetPropertyTenants } from "@tether-web-portal/api/property-tenants/hooks";
import { AssignTarget, AssignTenantModal } from "./AssignTenantModal";

interface UseTenantAssignmentMenuArgs {
  propertyId: string;
  kind: "outside" | "circuitNode";
  locationId: string;
  locationName: string;
  activeTenant?: ActiveTenantSummary | null;
  /**
   * Set when an ancestor/descendant node holds the tenant: the node can't be
   * assigned, so "Assign" explains why instead of opening the modal.
   */
  tenantBlocked?: TenantBlocked | null;
  /** Gate the whole thing on the TENANT_BILLING flag + edit permission. */
  enabled?: boolean;
}

/**
 * Tenant assign / change / clear actions for a location's kebab menu.
 *
 * Returns `menuItems` to drop into a `MenuList` and a `modal` element to
 * render alongside the menu (outside it, so it survives the menu closing).
 * The actions live in the existing kebab menu rather than on the badge, which
 * stays a display-only chip.
 */
export const useTenantAssignmentMenu = ({
  propertyId,
  kind,
  locationId,
  locationName,
  activeTenant,
  tenantBlocked,
  enabled,
}: UseTenantAssignmentMenuArgs): {
  menuItems: ReactNode;
  modal: ReactNode;
  /** Open the assign/change/clear modal directly (e.g. from a clickable chip). */
  open: (mode: AssignTarget["mode"]) => void;
  /** True when tenant actions are usable here (edit permission + property has tenants). */
  canAct: boolean;
} => {
  const labels = useTenantLabels();
  const singular = labels.singular.toLowerCase();
  const [assignTarget, setAssignTarget] = useState<AssignTarget | null>(null);

  const { data: tenants } = useGetPropertyTenants(propertyId, {
    enabled: Boolean(enabled && propertyId),
  });
  const propertyHasTenants = (tenants?.length ?? 0) > 0;

  const open = (mode: AssignTarget["mode"]) =>
    setAssignTarget({
      kind,
      id: locationId,
      name: locationName,
      mode,
      currentTenantId: activeTenant?.id,
    });

  // Only surface the tenant actions once the property has tenants set up —
  // there's nothing to assign/change to otherwise.
  let menuItems: ReactNode = null;
  if (enabled && propertyHasTenants) {
    menuItems = (
      <>
        <MenuDivider />
        {tenantBlocked ? (
          // A blocked node can't be assigned (a parent/child already holds the
          // tenant). Show a disabled item with the reason in a tooltip, rather
          // than an enabled item that only errors on click.
          <Tooltip
            label={`A ${tenantBlocked.by === "ancestor" ? "parent" : "child"} location (‘${tenantBlocked.nodeName}’) is already assigned to ${tenantBlocked.tenantName}.`}
            placement="left"
            shouldWrapChildren
          >
            <MenuItem isDisabled>Assign {singular}</MenuItem>
          </Tooltip>
        ) : activeTenant ? (
          <>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                open("change");
              }}
            >
              Change {singular}
            </MenuItem>
            <MenuItem
              color="red.500"
              onClick={(e) => {
                e.stopPropagation();
                open("end");
              }}
            >
              Clear {singular}
            </MenuItem>
          </>
        ) : (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              open("assign");
            }}
          >
            Assign {singular}
          </MenuItem>
        )}
      </>
    );
  }

  const modal = assignTarget ? (
    <AssignTenantModal
      propertyId={propertyId}
      target={assignTarget}
      isOpen={Boolean(assignTarget)}
      onClose={() => setAssignTarget(null)}
    />
  ) : null;

  return { menuItems, modal, open, canAct: Boolean(enabled && propertyHasTenants) };
};
